import { useState, useEffect } from 'react'
import { Center, User } from '../types'
import { useAuth } from '../hooks/useAuth'
import { loadCenters, createPurchaseRequest, uploadFile, loadPurchaseSupervisors } from '../lib/api'
import { canActAsPurchaseLeader } from './PurchaseRoleSelector'
import SupplierSelectorModal from './SupplierSelectorModal'
import './CreatePurchaseRequestForm.css'

interface CreatePurchaseRequestFormProps {
  onCancel: () => void
  onSuccess: () => void
}

function CreatePurchaseRequestForm({ onCancel, onSuccess }: CreatePurchaseRequestFormProps) {
  const { user } = useAuth()
  const isSelfLeader = canActAsPurchaseLeader(user)
  const [formData, setFormData] = useState({
    centerId: '',
    subject: '',
    description: '',
    reason: '',
    supplier: '',
    amount: '',
    file: null as File | null,
    supervisorId: '',
  })
  const [centers, setCenters] = useState<Center[]>([])
  const [supervisors, setSupervisors] = useState<User[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showSupplierModal, setShowSupplierModal] = useState(false)

  useEffect(() => {
    if (!user) return
    let cancelled = false

    const loadFormData = async () => {
      try {
        setLoading(true)
        const centersData = await loadCenters()
        if (cancelled) return
        setCenters(centersData)

        if (!canActAsPurchaseLeader(user)) {
          try {
            const sups = await loadPurchaseSupervisors()
            if (cancelled) return
            setSupervisors(sups.filter((s) => s.id !== user.id))
          } catch (supErr) {
            console.error('Chyba při načítání vedoucích:', supErr)
            if (!cancelled) {
              alert('Nepodařilo se načíst seznam vedoucích. Zkuste obnovit stránku.')
            }
          }
        }
      } catch (error) {
        console.error('Chyba při načítání dat:', error)
        if (!cancelled) {
          alert('Chyba při načítání dat formuláře')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadFormData()
    return () => {
      cancelled = true
    }
  }, [user])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.centerId || !formData.subject || !formData.description || !formData.reason || !user) {
      alert('Prosím vyplňte všechna povinná pole')
      return
    }
    if (!isSelfLeader && !formData.supervisorId) {
      alert('Vyberte svého vedoucího')
      return
    }

    setSubmitting(true)
    try {
      let fileUrl = null
      let fileName = null

      if (formData.file) {
        const uploadResult = await uploadFile(formData.file)
        fileUrl = uploadResult.file_url
        fileName = uploadResult.file_name
      }

      await createPurchaseRequest(
        formData.centerId,
        formData.subject,
        formData.description,
        formData.reason,
        formData.supplier || undefined,
        formData.amount ? parseFloat(formData.amount) : undefined,
        fileUrl || undefined,
        fileName || undefined,
        isSelfLeader ? null : formData.supervisorId
      )

      onSuccess()
    } catch (error: any) {
      console.error('Chyba při vytváření žádosti:', error)
      alert(error.message || 'Chyba při vytváření žádosti o nákup')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="create-purchase-request-form">
        <div className="loading">Načítání...</div>
      </div>
    )
  }

  return (
    <div className="create-purchase-request-form">
      <div className="form-container">
        <h2>Vytvořit žádost o nákup</h2>
        <form onSubmit={handleSubmit}>
          {!isSelfLeader && (
            <div className="form-group">
              <label htmlFor="supervisorId">
                Vyberte svého vedoucího <span className="required">*</span>
              </label>
              <select
                id="supervisorId"
                value={formData.supervisorId}
                onChange={(e) => setFormData({ ...formData, supervisorId: e.target.value })}
                required
              >
                <option value="">Vyberte vedoucího</option>
                {supervisors.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.role ? ` (${s.role})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          {isSelfLeader && (
            <div
              className="form-group"
              style={{
                padding: '0.75rem 1rem',
                background: '#ecfdf5',
                borderRadius: '8px',
                border: '1px solid #a7f3d0',
                color: '#065f46',
              }}
            >
              Jako vedoucí schválíte tuto žádost automaticky sami — výběr vedoucího není potřeba.
            </div>
          )}

          <div className="form-group">
            <label htmlFor="centerId">
              Název střediska <span className="required">*</span>
            </label>
            <select
              id="centerId"
              value={formData.centerId}
              onChange={(e) => setFormData({ ...formData, centerId: e.target.value })}
              required
            >
              <option value="">Vyberte středisko</option>
              {centers.map((center) => (
                <option key={center.id} value={center.id}>
                  {center.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="subject">
              Předmět žádosti <span className="required">*</span>
            </label>
            <input
              type="text"
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Např. Nákup kancelářských potřeb"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">
              Popis <span className="required">*</span>
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Podrobný popis předmětu žádosti"
              rows={5}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="reason">
              Důvod objednání <span className="required">*</span>
            </label>
            <textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Důvod, proč je tento nákup potřebný"
              rows={4}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="supplier">Dodavatel (předpoklad)</label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                id="supplier"
                value={formData.supplier}
                onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                placeholder="Název dodavatele (volitelné)"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={() => setShowSupplierModal(true)}
                className="supplier-select-button"
                title="Vybrat dodavatele ze seznamu"
              >
                📋 Ze seznamu
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="amount">ČÁSTKA v Kč vč. DPH</label>
            <input
              type="number"
              id="amount"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>

          <div className="form-group">
            <label htmlFor="file">Přílohy</label>
            <input
              type="file"
              id="file"
              onChange={(e) => setFormData({ ...formData, file: e.target.files?.[0] || null })}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
            />
            {formData.file && <p className="file-name">Vybraný soubor: {formData.file.name}</p>}
          </div>

          <div className="form-actions">
            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? 'Vytváření...' : 'Vytvořit žádost'}
            </button>
            <button type="button" onClick={onCancel} className="cancel-button" disabled={submitting}>
              Zrušit
            </button>
          </div>
        </form>
      </div>

      {showSupplierModal && (
        <SupplierSelectorModal
          onSelect={(supplierName) => {
            setFormData({ ...formData, supplier: supplierName })
            setShowSupplierModal(false)
          }}
          onClose={() => setShowSupplierModal(false)}
        />
      )}
    </div>
  )
}

export default CreatePurchaseRequestForm
